"use client";

import { useLenis } from "lenis/react";
import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useEffect } from "react";

import { SITE } from "@/constants/site";
import { Link } from "@/i18n/navigation";
import { cn, padNumber } from "@/lib/utils";

import type { NavItem } from "../../_constants/navigation";

import { LocaleSwitcher } from "./locale-switcher";
import { ThemeSwitcher } from "./theme-switcher";

// =============================================================================

const EASE = [0.76, 0, 0.24, 1] as const;

type MobileMenuProps = {
  items: NavItem[];
  activeHref: string | null;
  onClose: () => void;
};

/**
 * Full-screen menu of small screens: a curtain drops, then the links rise
 * one after another.
 */
export function MobileMenu({ items, activeHref, onClose }: MobileMenuProps) {
  const t = useTranslations("Navigation");
  const lenis = useLenis();

  // Freeze the page behind the menu and close it with Escape
  useEffect(() => {
    lenis?.stop();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      lenis?.start();
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [lenis, onClose]);

  return (
    <motion.div
      id="mobile-menu"
      role="dialog"
      aria-modal="true"
      aria-label={t("mainNavigation")}
      initial={{ clipPath: "inset(0 0 100% 0)" }}
      animate={{ clipPath: "inset(0 0 0% 0)" }}
      exit={{
        clipPath: "inset(0 0 100% 0)",
        transition: { duration: 0.6, ease: EASE, delay: 0.15 },
      }}
      transition={{ duration: 0.7, ease: EASE }}
      className="fixed inset-0 z-40 flex flex-col bg-background px-5 pt-28 pb-8 lg:hidden"
      data-lenis-prevent
    >
      <nav className="flex-1">
        <ul className="flex flex-col">
          {items.map((item, index) => {
            const isActive = item.href === activeHref;
            return (
              <li
                key={item.key}
                className="overflow-hidden border-b border-line first:border-t"
              >
                <motion.div
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  exit={{
                    y: "110%",
                    transition: { duration: 0.3, ease: EASE },
                  }}
                  transition={{
                    duration: 0.7,
                    ease: EASE,
                    delay: 0.25 + index * 0.05,
                  }}
                >
                  <Link
                    href={item.href}
                    onClick={onClose}
                    aria-current={isActive ? "page" : undefined}
                    className="group flex items-baseline gap-4 py-3.5"
                  >
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {padNumber(index + 1)}
                    </span>
                    <span
                      className={cn(
                        "text-[clamp(2rem,9vw,3rem)] leading-none font-bold tracking-tight uppercase transition-colors",
                        isActive ? "text-foreground" : "text-foreground/55"
                      )}
                    >
                      {t(item.key)}
                    </span>
                    {isActive && (
                      <span className="mb-1.5 size-2.5 self-end bg-brand" />
                    )}
                  </Link>
                </motion.div>
              </li>
            );
          })}
        </ul>
      </nav>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, transition: { duration: 0.2 } }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="flex items-center justify-between gap-4 pt-8"
      >
        <a
          href={`mailto:${SITE.email}`}
          className="truncate text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          {SITE.email}
        </a>
        <div className="flex shrink-0 items-center gap-2">
          <ThemeSwitcher />
          <LocaleSwitcher />
        </div>
      </motion.div>
    </motion.div>
  );
}

// =============================================================================
